import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type PurchaseStatus = "BORRADOR" | "PARCIAL" | "RECIBIDA" | "ANULADA";

/** BORRADOR es, en la práctica, una orden de compra pendiente de recibir: se crea con líneas y costos pero no toca inventario hasta `receivePurchase`. PARCIAL: ya llegó algo pero no todo (el proveedor entregó por partes) — se puede seguir recibiendo hasta completarla. Se reutilizan estos estados (no se crea una entidad nueva) — ver purchase.service.ts. */
export const PURCHASE_STATUS_LABEL: Record<PurchaseStatus, string> = {
  BORRADOR: "Orden pendiente",
  PARCIAL: "Recibida parcialmente",
  RECIBIDA: "Recibida",
  ANULADA: "Anulada",
};

export interface PurchaseLineRecord {
  id: string;
  productId: string;
  quantity: string;
  unitCost: string;
  subtotal: string;
  receivedQuantity: string;
  returnedQuantity: string;
  product?: { id: string; sku: string; name: string; unit: string };
}

export type PurchaseReturnReason = "DANADO" | "VENCIDO" | "NO_PEDIDO" | "CALIDAD_DEFECTUOSA" | "OTRO";

export const PURCHASE_RETURN_REASON_LABEL: Record<PurchaseReturnReason, string> = {
  DANADO: "Producto dañado",
  VENCIDO: "Producto vencido",
  NO_PEDIDO: "No fue lo pedido",
  CALIDAD_DEFECTUOSA: "Calidad defectuosa",
  OTRO: "Otro motivo",
};

export interface PurchaseRecord {
  id: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
  supplierId: string;
  invoiceNumber: string | null;
  purchaseDate: string;
  status: PurchaseStatus;
  subtotal: string;
  taxTotal: string;
  total: string;
  notes: string | null;
  createdAt: string;
  supplier?: { id: string; name: string; taxId: string | null };
  lines: PurchaseLineRecord[];
}

export interface ListPurchasesFilters {
  page?: number;
  pageSize?: number;
  supplierId?: string;
  status?: PurchaseStatus;
  branchId?: string;
}

export function usePurchases(filters: ListPurchasesFilters = {}) {
  return useQuery({
    queryKey: ["store", "purchases", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PurchaseRecord[]>>("/purchases", { params: filters });
      return data;
    },
  });
}

export interface CreatePurchasePayload {
  branchId: string;
  supplierId: string;
  purchaseDate: string;
  invoiceNumber?: string;
  notes?: string;
  taxTotal?: number | string;
  lines: Array<{ productId: string; quantity: number | string; unitCost: number | string }>;
}

export function useCreatePurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePurchasePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseRecord>>("/purchases", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "purchases"] }),
  });
}

export interface DraftPurchasesFromForecastPayload {
  branchId: string;
  items: Array<{ productId: string; quantity: number | string }>;
}

export interface DraftPurchasesFromForecastResult {
  createdPurchases: PurchaseRecord[];
  /** Productos seleccionados sin ninguna compra previa (no anulada) en la organización — no hay de dónde inferir el proveedor, así que quedan fuera de las órdenes generadas. */
  skippedProductIds: string[];
}

/** Genera órdenes de compra BORRADOR agrupadas por proveedor a partir del reporte de predicción de quiebre de stock — ver purchase.service.ts::createDraftPurchasesFromForecast. */
export function useCreateDraftPurchasesFromForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DraftPurchasesFromForecastPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<DraftPurchasesFromForecastResult>>("/purchases/draft-from-forecast", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "purchases"] }),
  });
}

export interface ReceivePurchasePayload {
  id: string;
  /** Si se omite, recibe todo lo pendiente de cada línea (recepción completa de un clic). Si se pasa, permite recepción parcial línea por línea. */
  lines?: Array<{ purchaseLineId: string; quantity: number | string }>;
}

export function useReceivePurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ReceivePurchasePayload | string) => {
      const { id, lines } = typeof payload === "string" ? { id: payload, lines: undefined } : payload;
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseRecord>>(`/purchases/${id}/receive`, lines ? { lines } : {});
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "purchases"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export function useVoidPurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseRecord>>(`/purchases/${id}/void`, {});
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "purchases"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export interface PurchaseReturnLineRecord {
  id: string;
  purchaseLineId: string;
  productId: string;
  quantity: string;
  unitCost: string;
  value: string;
  product?: { id: string; sku: string; name: string; unit: string };
}

export interface PurchaseReturnRecord {
  id: string;
  organizationId: string;
  purchaseId: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
  supplierId: string;
  supplier?: { id: string; name: string; taxId: string | null };
  reason: PurchaseReturnReason;
  totalValue: string;
  notes: string | null;
  createdAt: string;
  createdBy?: { id: string; fullName: string } | null;
  lines: PurchaseReturnLineRecord[];
}

export interface ListPurchaseReturnsFilters {
  page?: number;
  pageSize?: number;
  purchaseId?: string;
  supplierId?: string;
}

/** Historial de devoluciones a proveedor — endpoint separado (/purchases/returns) para no romper el shape de GET /purchases. Ver purchase.service.ts::listPurchaseReturns. */
export function usePurchaseReturns(filters: ListPurchaseReturnsFilters = {}) {
  return useQuery({
    queryKey: ["store", "purchase-returns", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PurchaseReturnRecord[]>>("/purchases/returns", { params: filters });
      return data;
    },
  });
}

export interface CreatePurchaseReturnPayload {
  purchaseId: string;
  reason: PurchaseReturnReason;
  notes?: string;
  lines: Array<{ purchaseLineId: string; quantity: number | string }>;
}

/** Devuelve mercancía ya recibida a su proveedor: descuenta inventario (DEVOLUCION_PROVEEDOR) y marca `returnedQuantity` en la línea de compra — ver purchase.service.ts::createPurchaseReturn. */
export function useCreatePurchaseReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePurchaseReturnPayload) => {
      const { purchaseId, ...body } = payload;
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseReturnRecord>>(`/purchases/${purchaseId}/returns`, body);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "purchases"] });
      queryClient.invalidateQueries({ queryKey: ["store", "purchase-returns"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}
